// Where a newly added element goes. Pure geometry: centres and sizes in canvas
// coordinates, nothing fabric-shaped, so it stays unit testable on its own.

// How far a new element is nudged off the one it would otherwise land on. The
// same step cloning uses: far enough to read as a second element and to grab
// without catching the one underneath, small enough to stay in the print area.
export const PLACEMENT_OFFSET = 40;

function samePoint(a, b) {
	return Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5;
}

// Keep a rect of `size` centred on `c` inside `bounds`. An element too big for
// the bounds on an axis stays centred on it - pushing it to an edge would only
// move the overflow to the other side.
function clampCenter(c, size, bounds) {
	if (!bounds) return c;
	const halfW = size.width / 2;
	const halfH = size.height / 2;
	const minX = bounds.left + halfW;
	const maxX = bounds.left + bounds.width - halfW;
	const minY = bounds.top + halfH;
	const maxY = bounds.top + bounds.height - halfH;
	return {
		x: minX > maxX ? bounds.left + bounds.width / 2 : Math.min(Math.max(c.x, minX), maxX),
		y: minY > maxY ? bounds.top + bounds.height / 2 : Math.min(Math.max(c.y, minY), maxY)
	};
}

function isTaken(c, taken, tolerance) {
	return taken.some(p => Math.abs(p.x - c.x) < tolerance && Math.abs(p.y - c.y) < tolerance);
}

// Centre for a new element that would otherwise be dropped exactly on top of one
// already there - a second text lands in the middle of the same print area as
// the first, hiding it, and the customer cannot tell there are two.
//
// Walks the diagonal in PLACEMENT_OFFSET steps until it finds a free spot, both
// ways so a print area whose centre sits against its bottom-right edge still has
// somewhere to go. Candidates are clamped into the bounds: a nudge may not push
// the element out of the print area, which a drag would never allow either.
export function cascadePosition({ center, size, bounds, taken = [], step = PLACEMENT_OFFSET, limit = 6 }) {
	const tolerance = step / 2;
	const base = clampCenter(center, size, bounds);
	if (!isTaken(base, taken, tolerance)) return base;

	// Nothing free: still hand back an offset spot so the new element is visible
	// as its own, rather than stacking it perfectly on the one underneath.
	let fallback = base;

	for (let i = 1; i <= limit; i++) {
		for (const dir of [1, -1]) {
			const d = i * step * dir;
			const c = clampCenter({ x: center.x + d, y: center.y + d }, size, bounds);
			// Clamped straight back onto the stack: this direction is out of room.
			if (samePoint(c, base)) continue;
			if (!isTaken(c, taken, tolerance)) return c;
			if (fallback === base) fallback = c;
		}
	}
	return fallback;
}
